/**
 * Phase 6 Vendor-Port W2 — GAS HEATER selector static data (§33.5).
 *
 * The param-chain GET URL builders (legacy checkDataExist getType/getSeries/
 * getHeatexMtl/getConfiguration/getVenting/getPilotConfig/getFuelType/
 * getFiringMode/getHeatexQty/getBurnerMTL/getBurnerConfig), the burner-assembly
 * GET, the count/catalog GETs, the collection master URLs, the defaults
 * (gasHeaterDefaults), and the required-field sets (REQUIRED_FIELDS[50]/[188]).
 *
 * Kept out of the 2,646-line component so the React selector reads cleanly.
 * Every URL is verbatim from gas_heater.selector.js / collection/*.js.
 *
 * NB the param chain is a DEPENDENT graph: type depends on mfg; series on
 * mfg+type; heatex_mtl/venting/pilot/configuration/fuel_type/burner_mtl on
 * mfg+type+series; firing_mode + tube_rows additionally on output_rating_std;
 * burner_config on mfg+type+series+burner_mtl+certification. The selector loads
 * each as the user picks parents (W2 read-only seeds the SAVED selection from
 * the model's gas_heater_params; the FULL dependency cascade narrowing matters
 * only once Rate fires — unreachable on dev3 read-only, see the selector).
 */

const CMS = '/api/v1.0/cms/1/v1.0.0/ahu';
const MASTER = '/api/v1.0/master/v1.0.0/ahu';

/** legacy gasHeaterDefaults (initialize). */
export const GH_DEFAULTS = {
  mfg_id: 50,
  type_id: 1,
  series_id: 296,
  fuel_type_id: 1,
} as const;

/** mfg 188 hides the count badge + available-sizes row + heat-exchanger subtab. */
export const GH_MFG_188 = 188;

/** Burner assembly "User Defined" — the one assembly whose burner list is the
 *  heater's OWN editable rows rather than the assembly's fixed set
 *  (`if (element == "1")`, gas_heater.selector.js:917). */
export const GH_BURNER_ASSY_USER_DEFINED = 1;

/**
 * The fields the Burner Assembly list depends on — every control legacy tags
 * `get_burner_assy` (the params/air-stream selects) or `get_burner_assy_model`
 * (the performance inputs) in gas_heater.selector.tmpl.
 *
 * Changing one marks the burner data dirty (`burner_data_change = true`, set by
 * `collectBurnerAssy` js:868 for the selects and by `changeModelField` js:1706
 * for the inputs), and the next `rateBurner` re-PATCHes and re-fetches the
 * list. That is what keeps the assemblies in step with the airflow and the
 * burner length the rate derives from these values — without it the list is
 * whatever the first rate returned, however stale.
 */
export const GH_BURNER_ASSY_DEPS = new Set<string>([
  // .get_burner_assy — tmpl:129/147/164/203/223/243/268/290/315/355/372/389/
  // 449/469/489/536/702/1023
  'mfg_id',
  'type_id',
  'series_id',
  'power_id',
  'weather_rating_id',
  'certification_id',
  'venting_id',
  'conn_hand_id',
  'heatex_mtl_id',
  'tube_rows_vertically',
  'configuration',
  'heatex_qty_id',
  'burner_mtl_id',
  'fuel_type_id',
  'firing_mode_id',
  'pilot_id',
  'air_stream_id',
  'blank_off_id',
  // .get_burner_assy_model — tmpl:507/521/686/763/777/791
  'gas_inlet_pressure',
  'burner_manifold_pressure_conditional',
  'airflow_actual',
  'eadb_temp',
  'ladb_temp_desired',
  'capacity_required_total',
]);

/** One row of the direct-fire burner builder's editable list. `id` is the
 *  saved AHUGasHeaterBurner pk; a row added with + ADD has none until Rate. */
export interface BurnerRow {
  key: string;
  id?: number;
  burner_config_id?: number;
}

/**
 * Param-chain GET URL builders (legacy checkDataExist family). All are
 * mapToCMS:false GETs cached per-URL (RTKQ does the stateStorage
 * 'gas-heater-rating-dialog' URL caching natively).
 */
export const ghParamUrls = {
  type: (mfg_id: number | string) =>
    `${CMS}/gas_heaters/params/type/?mfg_id=${mfg_id}`,
  series: (mfg_id: number | string, type_id: number | string) =>
    `${CMS}/gas_heaters/params/series/?mfg_id=${mfg_id}&type_id=${type_id}`,
  heatex_mtl: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/heatex_mtl/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  tube_rows_vertically: (
    mfg_id: number | string,
    type_id: number | string,
    series_id: number | string,
    output_rating_std: number | string
  ) =>
    `${CMS}/gas_heaters/params/tube_rows_vertically/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}&output_rating_std=${output_rating_std}`,
  configuration: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/configuration/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  burner_mtl: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/burner_mtl/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  fuel_type: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/fuel_type/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  firing_mode: (
    mfg_id: number | string,
    type_id: number | string,
    series_id: number | string,
    output_rating_std: number | string = ''
  ) =>
    `${CMS}/gas_heaters/params/firing_mode/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}${
      output_rating_std ? `&output_rating_std=${output_rating_std}` : ''
    }`,
  pilot_config: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/pilot_config/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  venting: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/venting/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  heatex_qty: (mfg_id: number | string, type_id: number | string, series_id: number | string) =>
    `${CMS}/gas_heaters/params/heatex_qty/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}`,
  burner_config: (
    mfg_id: number | string,
    type_id: number | string,
    series_id: number | string,
    burner_mtl_id: number | string,
    certification_id: number | string
  ) =>
    `${CMS}/gas_heaters/params/burner_config/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}&burner_mtl_id=${burner_mtl_id}&certification_id=${certification_id}`,
  /** output_rating_std list = the capacity model (legacy capacity_model). */
  output_rating_std: (
    mfg_id: number | string,
    type_id: number | string,
    series_id: number | string,
    capacity_required: number | string
  ) =>
    `${CMS}/gas_heaters/params/output_rating_std/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}&capacity_required=${capacity_required}`,
  /** qty_high/qty_wide ranges by type (legacy qtyCollection). */
  qty: (type_id: number | string) => `${CMS}/gas_heaters/qty/type/${type_id}`,
  /**
   * Burner assemblies (legacy getBurnerAssy — master collection, js:443-450).
   *
   * `min_length` is the `burner_length_required_min` the rate PATCH just
   * computed, and it MUST be interpolated the way legacy interpolates it,
   * including when it is null. The backend filter
   * (master/filter/gas_heater.py:80-90) branches three ways:
   *
   *   absent / ''      no length filter at all — every assembly the airflow
   *                    allows, INCLUDING ones whose burners are too short.
   *   'null' / 'None'  only the `user_defined` row.
   *   a number         `user_defined` + assemblies at least that long.
   *
   * `burner_length_required_min` is a computed property that returns None
   * whenever the heater has no output rating yet
   * (models_component_gas_heater.py:487-490), so it arrives as JSON `null`
   * often. Legacy's template literal turns that into the STRING `"null"` and
   * the list collapses to User Defined — correct, because with no required
   * length there is no way to know which assemblies fit. Passing `''` instead
   * (an earlier port) silently took the first branch and offered — and
   * auto-selected — assemblies the burner cannot fill. So: pass the value
   * through, do not coalesce it to an empty string.
   */
  burner_assy: (
    mfg_id: number | string,
    type_id: number | string,
    series_id: number | string,
    airflow_actual: number | string | null,
    min_length: number | string | null
  ) =>
    airflow_actual != null
      ? `${MASTER}/gh_burner_assy/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}&airflow_actual=${airflow_actual}&min_length=${min_length}`
      : `${MASTER}/gh_burner_assy/?mfg_id=${mfg_id}&type_id=${type_id}&series_id=${series_id}&min_length=${min_length}`,
} as const;

/**
 * Availability COUNT + CATALOG-page URLs. The legacy collectData builds a query
 * string from params_gas_heater (skipping type-3 keys, renaming heatex_qty_id →
 * heatex_qty). The catalog page (loadParams) walks gas_heaters_list.
 */
export const ghCountUrl = (filters: string) =>
  `${CMS}/gas_heaters/all/count/?${filters}`.replace('heatex_qty_id', 'heatex_qty');
export const ghCatalogUrl = (filters: string) =>
  `${CMS}/gas_heaters/all/?${filters}`.replace('heatex_qty_id', 'heatex_qty');
export const GH_ALL_COUNT_URL = `${CMS}/gas_heaters/all/count/?`;

/**
 * The `.search_field` selects legacy collectData() walks to build the count
 * query (gas_heater.selector.js:2506-2573), in template DOM order. There are
 * exactly 18 of them in gas_heater.selector.tmpl and they are ALL selects —
 * `collectData` gathers under `if (elem.prop('tagName') == 'SELECT')`.
 *
 * Do NOT source this from the rate payload's params_gas_heater blob: that blob
 * additionally carries pilot_config_id / assy_height_from_floor /
 * burner_config_id, none of which are `.search_field`s, and the air_stream /
 * season / blank_off / pricing selects are excluded too. Sending them would
 * over-filter the catalog and under-report the count.
 */
export const GH_COUNT_FIELDS = [
  'mfg_id',
  'type_id',
  'series_id',
  'power_id',
  'weather_rating_id',
  'certification_id',
  'venting_id',
  'conn_hand_id',
  'heatex_mtl_id',
  'tube_rows_vertically',
  'configuration',
  'heatex_qty_id',
  'burner_mtl_id',
  'fuel_type_id',
  'firing_mode_id',
  'qty_wide',
  'qty_high',
  'output_rating_std',
] as const;

/**
 * Build the availability-count filter string, reproducing collectData's skip
 * rules exactly:
 *   - mfg 188 drops tube_rows_vertically / heatex_mtl_id / heatex_qty_id (:2511)
 *   - type 3 drops conn_hand_id / fuel_type_id / firing_mode_id /
 *     output_rating_std (deleted at :2541-2546) plus power_id / heatex_qty_id /
 *     heatex_mtl_id (skipped in the string loop at :2557-2562). NB legacy's
 *     `delete params.power_id` is commented out, but the string loop skips
 *     power_id anyway — net effect is the same, so it is excluded here.
 *   - qty_wide == 2 drops conn_hand_id (:2556)
 *   - null/undefined values are omitted (:2558)
 *   - type 1 prefixes `elevation=<project elevation>&` (:2554)
 * The trailing `&` is legacy's too; `ghCountUrl` appends it to a bare `?`.
 *
 * `emptyFields` carries collectData's one remaining skip rule, the one that is
 * implicit in it reading the DOM rather than a model: it gathers `elem.val()`,
 * and a <select> with no <option> children yields `null`, so that field drops
 * out of the string (:2558). A React component keeps the last value it was
 * handed whether or not the current list still offers it, so the caller has to
 * say which selects are currently empty. It matters on a Manufacturer change:
 * Heatco→Midco leaves venting_id 1 and configuration "A" in state while
 * `params/venting/` and `params/configuration/` both return `[]` for Midco, and
 * sending either narrows the catalogue to nothing — dev sends
 * `mfg_id=188&type_id=3&series_id=1068&weather_rating_id=1&certification_id=1&
 * burner_mtl_id=3583&` and no more.
 */
export const ghCountFilters = (
  s: Record<string, unknown>,
  elevation: number | string,
  emptyFields?: ReadonlySet<string>
): string => {
  const mfg = Number(s.mfg_id);
  const type = Number(s.type_id);
  const isType3 = type === 3;
  const wideIsTwo = Number(s.qty_wide) === 2;
  let str = type === 1 ? `elevation=${elevation}&` : '';
  for (const key of GH_COUNT_FIELDS) {
    const val = s[key];
    if (val == null || val === '') continue;
    if (emptyFields?.has(key)) continue;
    if (
      mfg === GH_MFG_188 &&
      (key === 'tube_rows_vertically' || key === 'heatex_mtl_id' || key === 'heatex_qty_id')
    ) {
      continue;
    }
    if (
      isType3 &&
      (key === 'power_id' ||
        key === 'conn_hand_id' ||
        key === 'fuel_type_id' ||
        key === 'firing_mode_id' ||
        key === 'output_rating_std' ||
        key === 'heatex_qty_id' ||
        key === 'heatex_mtl_id')
    ) {
      continue;
    }
    if (wideIsTwo && key === 'conn_hand_id') continue;
    str += `${key}=${val}&`;
  }
  return str;
};

/** Static master collection URLs (legacy collection/*.js). */
export const GH_COLLECTION_URLS = {
  fuel_type: `${MASTER}/gas_heater_fuel_type/`,
  firing_mode: `${MASTER}/gas_heater_firing_mode/`,
  venting: `${MASTER}/gas_heater_venting/`,
  voltages: `${MASTER}/voltages/`,
  weather_rating: `${CMS}/weather_ratings/`,
  // MASTER already ends in `/ahu`; the legacy collection URL is
  // `master/v1.0.0/ahu/pricing_methods/` (a single `ahu`). The earlier
  // `${MASTER}/ahu/pricing_methods/` produced a double `ahu/ahu/` → HTTP 404,
  // leaving the Pricing-tab Method dropdown empty.
  pricing_methods: `${MASTER}/pricing_methods/`,
} as const;

/**
 * Required fields gating #rate, per mfg (legacy REQUIRED_FIELDS[50]/[188]).
 * mfg 50 (Modine/Midco tubular) requires the same airflow/temps + a chosen
 * output_rating_std (the legacy `input_rating_std` select must have a value).
 */
export const GH_REQUIRED_FIELDS: Record<number, Set<string>> = {
  50: new Set([
    'airflow_actual',
    'air_stream_id',
    'burner_manifold_pressure_conditional',
    'eadb_temp',
    'ladb_temp_desired',
  ]),
  188: new Set([
    'airflow_actual',
    'air_stream_id',
    'eadb_temp',
    'ladb_temp_desired',
    'burner_manifold_pressure_conditional',
  ]),
};

/**
 * The doc-resolved STATIC selects (resolved via gasHeaterCms — gas_heater doc
 * or global cms doc). Everything else in the param chain comes from the param
 * GETs (ghParamUrls) and renders with its own row shape.
 */
export const GH_DOC_RESOLVED_FIELDS = new Set<string>([
  'mfg_id',
  'power_id',
  'conn_hand_id',
  'certification_id',
  'blank_off_id',
  'air_stream_id',
  'weather_rating_id',
]);

/** number-input descriptors (data-min/max/step) for the performance inputs. */
export const GH_NUMBER_FIELDS = {
  airflow_actual: { formatter: 'integer' as const },
  eadb_temp: { formatter: 'decimal' as const, max: 195 },
  ladb_temp_desired: { formatter: 'decimal' as const, min: 40, max: 200 },
  capacity_required_total: { formatter: 'integer' as const },
  gas_inlet_pressure: { formatter: 'decimal' as const },
  burner_manifold_pressure_conditional: { formatter: 'decimal' as const },
  assy_height_from_floor: { formatter: 'decimal' as const },
  assy_distance_from_left_wall: { formatter: 'decimal' as const },
};
